/**
 * T3 — 화재잔해 클래스 모드 작업공간 (사건 단위, 최소형).
 *
 * 기획 v4 §4 두 모드 분리: 이 화면은 ① 화재잔해 클래스 모드다.
 * 기존 분석 대시보드(/analyses/{id}/dashboard)는 ② 위해기체·단일화합물
 * 모드로 유지되며, 두 모드는 화면·흐름을 공유하지 않는다.
 *
 * 권장 흐름: 사건·시료 → TIC/EIP 와 대조군 검토 → 클래스/탄소범위 후보
 *          → 진단 화합물 확인(단일화합물 모드 링크) → 참조 제품 비교(외부
 *            DB 확보 대기) → 감정인 결론(사람 입력 판정만 기록).
 *
 * 이 화면은 어떤 판정도 자동으로 만들지 않는다 — 스크리닝 계산 결과와
 * 감정인이 직접 기록한 판정만 보여준다.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/Breadcrumb";
import RtOverlayChart, {
  type RtSeries,
} from "@/components/charts/RtOverlayChart";
import { useAnalysesByCase } from "@/hooks/useAnalysis";
import { useAuth } from "@/hooks/useAuth";
import { useCaseDetail } from "@/hooks/useCases";
import {
  useClassAssessments,
  useCreateClassAssessment,
  useILClassScreening,
  useSampleEIP,
} from "@/hooks/useFireDebris";
import { useSampleList } from "@/hooks/useSamples";
import { useSpectrumTIC } from "@/hooks/useSpectrumData";
import type { AssessmentStatus, ILClassCandidate } from "@/types";
import {
  ASSESSMENT_STATUS_LABEL,
  assessmentBadgeClass,
} from "@/utils/assessment";

// EIP 스크리닝 이온 고정 색 (dataviz 검증기 6종 검사 전부 통과 — 순서 고정).
const EIP_ION_COLORS: Record<number, string> = {
  57: "#6d28d9",
  69: "#ea580c",
  70: "#0d9488",
  81: "#a16207",
  82: "#0284c7",
  83: "#991b1b",
  91: "#4d7c0f",
};
const EIP_FALLBACK_COLOR = "#475569";

// TIC 겹쳐보기 — 기존 스펙트럼 비교 규약(실험 red / 참조 blue)을 따른다.
const TIC_SAMPLE_COLOR = "#dc2626";
const TIC_CONTROL_COLOR = "#2563eb";

const ASSESSMENT_STATUSES: AssessmentStatus[] = [
  "identified",
  "partially_consistent",
  "not_classifiable",
  "insufficient_data",
  "library_not_evaluated",
];

function CandidateConditionList({ items }: { items: string[] }) {
  if (items.length === 0) return null;
  return (
    <ul className="list-disc list-inside text-xs text-gray-600 space-y-0.5">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

export default function FireDebrisWorkspacePage() {
  const { id } = useParams();
  const caseId = Number(id);
  const { permissions } = useAuth();

  const { data: caseData, isLoading: caseLoading } = useCaseDetail(caseId);
  // limit 은 백엔드 상한(100) 명시 — 기본 20 이면 20개 초과 사건에서
  // 오래된 시료가 조용히 잘린다 (교차 리뷰)
  const { data: samples, isLoading: samplesLoading } = useSampleList({
    case_id: caseId,
    limit: 100,
  });
  const { data: analyses } = useAnalysesByCase(caseId);

  const [selectedSampleId, setSelectedSampleId] = useState<number | null>(null);
  const [controlSampleId, setControlSampleId] = useState<number | null>(null);
  const [enabledIons, setEnabledIons] = useState<Set<number>>(
    () => new Set(Object.keys(EIP_ION_COLORS).map(Number)),
  );

  const sampleId = selectedSampleId ?? samples?.[0]?.id ?? null;

  // 검토 시료 변경으로 대조군과 같아지면 대조군은 무효 — 자기 자신과의
  // 비교를 '대조군'으로 그리지 않는다 (교차 리뷰)
  const effectiveControlId =
    controlSampleId !== null && controlSampleId !== sampleId
      ? controlSampleId
      : null;

  // 선택 시료의 최신 완료 분석 — 클래스 스크리닝·판정의 대상
  const latestCompletedAnalysis = useMemo(() => {
    if (!sampleId || !analyses) return null;
    const completed = analyses.filter(
      (a) => a.sample_id === sampleId && a.status === "completed",
    );
    if (completed.length === 0) return null;
    return [...completed].sort((a, b) =>
      (b.created_at ?? "").localeCompare(a.created_at ?? ""),
    )[0];
  }, [sampleId, analyses]);

  const ticQuery = useSpectrumTIC(sampleId ?? undefined);
  const controlTicQuery = useSpectrumTIC(effectiveControlId ?? undefined);
  const eipQuery = useSampleEIP(sampleId ?? undefined);
  const screeningQuery = useILClassScreening(
    latestCompletedAnalysis?.id ?? undefined,
  );
  const assessmentsQuery = useClassAssessments(
    latestCompletedAnalysis?.id ?? undefined,
  );
  const createAssessment = useCreateClassAssessment(
    latestCompletedAnalysis?.id ?? undefined,
  );

  // 판정 입력 폼 상태 — 실패 시 입력을 보존한다 (T2 UX 규칙)
  const [formClassKey, setFormClassKey] = useState("");
  const [formStatus, setFormStatus] = useState<AssessmentStatus | "">("");
  const [formRationale, setFormRationale] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  // 시료/분석 전환 시 폼 초기화 — 다른 분석에 판정이 오귀속되면
  // append-only 라 영구 기록이 된다 (교차 리뷰)
  const analysisIdForForm = latestCompletedAnalysis?.id ?? null;
  useEffect(() => {
    setFormClassKey("");
    setFormStatus("");
    setFormRationale("");
    setFormError(null);
  }, [analysisIdForForm]);

  const sampleName = (sid: number | null) =>
    samples?.find((s) => s.id === sid)?.name ?? (sid ? `시료 #${sid}` : "");

  const ticSeries: RtSeries[] = [];
  if (ticQuery.data && ticQuery.data.length > 0 && sampleId) {
    ticSeries.push({
      key: "sample",
      label: `검토 시료 TIC — ${sampleName(sampleId)}`,
      color: TIC_SAMPLE_COLOR,
      points: ticQuery.data.map((p) => ({ rt: p.rt, intensity: p.intensity })),
    });
  }
  if (
    controlTicQuery.data &&
    controlTicQuery.data.length > 0 &&
    effectiveControlId
  ) {
    ticSeries.push({
      key: "control",
      label: `대조군 TIC — ${sampleName(effectiveControlId)}`,
      color: TIC_CONTROL_COLOR,
      strokeDasharray: "6 3",
      points: controlTicQuery.data.map((p) => ({
        rt: p.rt,
        intensity: p.intensity,
      })),
    });
  }

  const eip = eipQuery.data;

  // 토글 상태는 서버가 준 이온 목록으로 시드한다 — 서버 이온 변경 시
  // 새 이온이 초기 숨김되지 않도록 (교차 리뷰)
  const serverIonKey = (eip?.ions ?? []).map((i) => i.mz).join(",");
  useEffect(() => {
    if (eip?.available && eip.ions.length > 0) {
      setEnabledIons(new Set(eip.ions.map((i) => i.mz)));
    }
    // eip 객체 대신 이온 목록 키로만 반응한다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverIonKey]);

  const eipSeries: RtSeries[] = (eip?.ions ?? [])
    .filter((ion) => enabledIons.has(ion.mz))
    .map((ion) => ({
      key: `mz-${ion.mz}`,
      label: `m/z ${ion.mz}`,
      color: EIP_ION_COLORS[ion.mz] ?? EIP_FALLBACK_COLOR,
      points: ion.points,
    }));

  const screening = screeningQuery.data;

  // (스키마, 클래스)별 현재 판정 — 화면의 스크리닝 스키마와 일치하는 것만
  const currentByClass = useMemo(() => {
    const map = new Map<
      string,
      NonNullable<typeof assessmentsQuery.data>["current"][number]
    >();
    if (assessmentsQuery.data && screening) {
      for (const a of assessmentsQuery.data.current) {
        if (a.scheme_id === screening.scheme.scheme_id) {
          map.set(a.class_key, a);
        }
      }
    }
    return map;
  }, [assessmentsQuery.data, screening]);

  const toggleIon = (mz: number) => {
    setEnabledIons((prev) => {
      const next = new Set(prev);
      if (next.has(mz)) next.delete(mz);
      else next.add(mz);
      return next;
    });
  };

  const handleSubmitAssessment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!screening || !formClassKey || !formStatus || !formRationale.trim()) {
      setFormError("클래스·판정 상태·근거를 모두 입력해 주십시오.");
      return;
    }
    setFormError(null);
    try {
      await createAssessment.mutateAsync({
        scheme_id: screening.scheme.scheme_id,
        class_key: formClassKey,
        status: formStatus,
        rationale: formRationale.trim(),
      });
      // 성공 시에만 초기화 (실패 시 근거 보존)
      setFormClassKey("");
      setFormStatus("");
      setFormRationale("");
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "판정 기록에 실패했습니다. 입력은 보존됩니다.";
      setFormError(String(detail));
    }
  };

  if (caseLoading || samplesLoading) {
    return (
      <div className="animate-pulse space-y-4 p-4">
        <div className="h-8 bg-gray-200 rounded w-1/3" />
        <div className="h-64 bg-gray-200 rounded" />
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb
        items={[
          { label: "대시보드", to: "/" },
          { label: "사건관리", to: "/cases" },
          { label: caseData?.case_number ?? `사건 #${id}`, to: `/cases/${id}` },
          { label: "화재잔해 클래스 모드" },
        ]}
      />

      {/* 모드 배너 — 두 모드 분리 (기획 v4 §4) */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              화재잔해 클래스 모드
              <span className="ml-2 text-sm font-medium text-amber-700 align-middle">
                스크리닝 보조 화면
              </span>
            </h1>
            <p className="text-sm text-gray-700 mt-1">
              인화성 액체 <strong>클래스</strong> 검토를 위한 작업공간입니다
              (TIC/EIP·대조군 → 클래스 조건 → 진단 화합물 → 감정인 판정 기록).
              화합물 단위 검토는 <strong>위해기체·단일화합물 모드</strong>(분석
              대시보드)에서 별도로 수행합니다. 이 화면은 어떤 판정도 자동으로
              만들지 않습니다.
            </p>
          </div>
          <Link
            to={`/cases/${id}`}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap"
          >
            ← 사건 상세로
          </Link>
        </div>
      </div>

      {!samples || samples.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
          이 사건에 등록된 시료가 없습니다. 시료를 먼저 등록해 주십시오.
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* ===== 좌: 시료 선택 + TIC/EIP ===== */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="fd-sample"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    검토 시료
                  </label>
                  <select
                    id="fd-sample"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    value={sampleId ?? ""}
                    onChange={(e) =>
                      setSelectedSampleId(Number(e.target.value))
                    }
                  >
                    {samples.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.sample_id})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="fd-control"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    대조군·비교 시료 (선택)
                  </label>
                  <select
                    id="fd-control"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    value={effectiveControlId ?? ""}
                    onChange={(e) =>
                      setControlSampleId(
                        e.target.value ? Number(e.target.value) : null,
                      )
                    }
                  >
                    <option value="">선택 안 함</option>
                    {samples
                      .filter((s) => s.id !== sampleId)
                      .map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name} ({s.sample_id})
                        </option>
                      ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    시료 등록에 대조군 구분 필드는 아직 없습니다 — 같은 사건의
                    다른 시료를 비교 목적으로 겹쳐볼 수 있습니다.
                  </p>
                  {samples.length >= 100 && (
                    <p className="text-xs text-amber-700 mt-1">
                      ⚠ 최근 100개 시료만 표시 중입니다 — 이 사건에 시료가 더
                      있을 수 있습니다.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                <span className="w-2 h-2 bg-red-500 rounded-full mr-2" />
                TIC 겹쳐보기
                <span className="ml-2 text-sm font-normal text-gray-500">
                  (검토 시료 vs 대조군)
                </span>
              </h3>
              <RtOverlayChart
                series={ticSeries}
                height={260}
                ariaLabel="검토 시료와 대조군의 TIC 겹쳐보기"
                emptyMessage={
                  ticQuery.isLoading
                    ? "TIC 불러오는 중…"
                    : "이 시료에는 TIC 데이터가 없습니다."
                }
              />
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center">
                <span className="w-2 h-2 bg-violet-700 rounded-full mr-2" />
                EIP (추출이온 프로파일)
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                판별표 스크리닝 이온 기준. 레퍼런스 IL 과의 시각적 패턴 비교는
                감정인의 판단 영역입니다 — 이 차트는 판정하지 않습니다.
              </p>
              {eipQuery.isLoading ? (
                <div className="h-40 flex items-center justify-center text-sm text-gray-500">
                  EIP 계산 중…
                </div>
              ) : eip && !eip.available ? (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
                  {eip.reason_ko ?? "EIP 를 계산할 수 없습니다."}
                </div>
              ) : eip ? (
                <>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {eip.ions.map((ion) => (
                      <button
                        key={ion.mz}
                        type="button"
                        onClick={() => toggleIon(ion.mz)}
                        title={ion.purpose_ko ?? undefined}
                        className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${
                          enabledIons.has(ion.mz)
                            ? "bg-gray-50 border-gray-300 text-gray-900"
                            : "bg-white border-gray-200 text-gray-400"
                        }`}
                      >
                        <span
                          className="w-2 h-2 rounded-full mr-1.5"
                          style={{
                            backgroundColor: enabledIons.has(ion.mz)
                              ? (EIP_ION_COLORS[ion.mz] ?? EIP_FALLBACK_COLOR)
                              : "#d1d5db",
                          }}
                        />
                        m/z {ion.mz}
                      </button>
                    ))}
                  </div>
                  <RtOverlayChart
                    series={eipSeries}
                    height={280}
                    ariaLabel="추출이온 프로파일 차트"
                    emptyMessage="표시할 이온을 선택해 주십시오."
                  />
                  <p className="text-xs text-gray-400 mt-2">
                    스캔 {eip.scan_count}개 · m/z 허용오차 ±{eip.tolerance}
                  </p>
                </>
              ) : (
                <div className="h-40 flex items-center justify-center text-sm text-gray-500">
                  EIP 데이터를 불러오지 못했습니다.
                </div>
              )}
            </div>
          </div>

          {/* ===== 우: 클래스 스크리닝 + 판정 ===== */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                <span className="w-2 h-2 bg-sky-600 rounded-full mr-2" />
                클래스/탄소범위 후보
                <span className="ml-2 text-sm font-normal text-gray-500">
                  (조건 충족 계산 — 판정 아님)
                </span>
              </h3>

              {!latestCompletedAnalysis ? (
                <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm text-gray-600">
                  이 시료에는 완료된 분석이 없어 클래스 스크리닝을 계산할 수
                  없습니다.
                </div>
              ) : screeningQuery.isLoading ? (
                <div className="h-24 flex items-center justify-center text-sm text-gray-500">
                  스크리닝 계산 중…
                </div>
              ) : screening ? (
                <div className="space-y-4">
                  {/* 스키마 식별 + 표준판 대기 공지 */}
                  <div className="bg-gray-50 border border-gray-200 rounded p-3 text-xs text-gray-600 space-y-1">
                    <p>
                      <span className="font-medium text-gray-800">
                        대상 분석:
                      </span>{" "}
                      #{latestCompletedAnalysis.id}
                      {latestCompletedAnalysis.completed_at
                        ? ` (완료 ${new Date(latestCompletedAnalysis.completed_at).toLocaleString("ko-KR")})`
                        : ""}
                    </p>
                    <p>
                      <span className="font-medium text-gray-800">
                        적용 스키마:
                      </span>{" "}
                      {screening.scheme.scheme_id} ·{" "}
                      {screening.scheme.standard_ref}
                    </p>
                    {screening.scheme.source_note_ko && (
                      <p>{screening.scheme.source_note_ko}</p>
                    )}
                    {screening.pending_scheme?.note_ko && (
                      <p className="text-amber-700">
                        ⚠ {screening.pending_scheme.note_ko}
                      </p>
                    )}
                  </div>

                  <p className="text-sm text-gray-800">
                    {screening.screening.summary_ko}
                  </p>

                  {screening.screening.matrix_warnings.map((w) => (
                    <div
                      key={w}
                      className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800"
                    >
                      ⚠ {w}
                    </div>
                  ))}

                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                          <th className="py-2 pr-4">클래스</th>
                          <th className="py-2 pr-4">조건</th>
                          <th className="py-2 pr-4">근거·미충족 사유</th>
                          <th className="py-2">판정(감정인)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {screening.screening.candidates.map(
                          (c: ILClassCandidate) => {
                            const current = currentByClass.get(c.class_key);
                            return (
                              <tr
                                key={c.class_key}
                                className="border-b border-gray-100 align-top"
                              >
                                <td className="py-2 pr-4 font-medium text-gray-900">
                                  {c.label_ko}
                                </td>
                                <td className="py-2 pr-4">
                                  <span
                                    className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                      c.required_met
                                        ? "bg-sky-100 text-sky-800"
                                        : "bg-gray-100 text-gray-600"
                                    }`}
                                  >
                                    {c.required_met ? "조건 충족" : "미충족"}
                                  </span>
                                </td>
                                <td className="py-2 pr-4 space-y-1">
                                  {/* 후보 존재/부재이지 '검출/확인'이 아니다 */}
                                  {c.matched_required.length > 0 && (
                                    <p className="text-xs text-gray-700">
                                      AI 후보에 있음:{" "}
                                      {c.matched_required.join(", ")}
                                    </p>
                                  )}
                                  {c.missing_required.length > 0 && (
                                    <p className="text-xs text-gray-500">
                                      후보에 없음:{" "}
                                      {c.missing_required.join(", ")}
                                    </p>
                                  )}
                                  <CandidateConditionList
                                    items={c.failed_conditions}
                                  />
                                  {c.notes.length > 0 && (
                                    <ul className="list-disc list-inside text-xs text-amber-700 space-y-0.5">
                                      {c.notes.map((n) => (
                                        <li key={n}>{n}</li>
                                      ))}
                                    </ul>
                                  )}
                                </td>
                                <td className="py-2">
                                  {current ? (
                                    <span
                                      className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${assessmentBadgeClass(current.status)}`}
                                      title={current.rationale}
                                    >
                                      {ASSESSMENT_STATUS_LABEL[
                                        current.status
                                      ] ?? current.status}
                                    </span>
                                  ) : assessmentsQuery.isLoading ? (
                                    <span className="text-xs text-gray-400">
                                      불러오는 중…
                                    </span>
                                  ) : assessmentsQuery.isError ? (
                                    <span className="text-xs text-red-500">
                                      판정 기록 불러오기 실패
                                    </span>
                                  ) : (
                                    <span className="text-xs text-gray-400">
                                      기록 없음
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          },
                        )}
                      </tbody>
                    </table>
                  </div>

                  <p className="text-xs text-gray-500">
                    입력: 관찰(스캔) {screening.input.observation_count}건 ·
                    기록된 후보 행 {screening.input.candidate_count}건 · 고유
                    화합물 {screening.input.distinct_compound_count}종
                    {screening.input.unrecognized_compound_count > 0 &&
                      ` · 어휘 밖(해석 불가) 이름 ${screening.input.unrecognized_compound_count}종`}{" "}
                    — {screening.input.basis_note_ko}
                  </p>
                  <p className="text-xs text-gray-400 border-t border-gray-100 pt-2">
                    {screening.screening.disclaimer_ko}
                  </p>

                  <Link
                    to={`/analyses/${latestCompletedAnalysis.id}/dashboard`}
                    className="inline-block text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    진단 화합물 확인 — 단일화합물 모드 대시보드 →
                  </Link>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm text-gray-600">
                  클래스 스크리닝을 불러오지 못했습니다.
                </div>
              )}
            </div>

            {/* 판정 기록 (사람 입력만) — 이력은 스크리닝 성패와 무관하게
                보인다: 사람이 기록한 판정이 계산 오류에 가려지면 안 된다 */}
            {latestCompletedAnalysis && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                  <span className="w-2 h-2 bg-green-600 rounded-full mr-2" />
                  클래스 판정 기록
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    (감정인 입력 — append-only, 정정은 새 판정으로)
                  </span>
                </h3>

                {permissions?.canAssessCandidates && !screening ? (
                  <p className="text-sm text-amber-700">
                    스크리닝 응답을 불러오지 못해 판정 입력이 비활성화되었습니다
                    (적용 스키마를 확인할 수 없습니다). 기록된 판정 이력은
                    아래에 표시됩니다.
                  </p>
                ) : permissions?.canAssessCandidates && screening ? (
                  <form onSubmit={handleSubmitAssessment} className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label
                          htmlFor="fd-assess-class"
                          className="block text-xs font-medium text-gray-700 mb-1"
                        >
                          클래스
                        </label>
                        <select
                          id="fd-assess-class"
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          value={formClassKey}
                          onChange={(e) => setFormClassKey(e.target.value)}
                        >
                          <option value="">선택</option>
                          {screening.screening.candidates.map((c) => (
                            <option key={c.class_key} value={c.class_key}>
                              {c.label_ko}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label
                          htmlFor="fd-assess-status"
                          className="block text-xs font-medium text-gray-700 mb-1"
                        >
                          판정 상태
                        </label>
                        <select
                          id="fd-assess-status"
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          value={formStatus}
                          onChange={(e) =>
                            setFormStatus(e.target.value as AssessmentStatus)
                          }
                        >
                          <option value="">선택</option>
                          {ASSESSMENT_STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {ASSESSMENT_STATUS_LABEL[s]}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="fd-assess-rationale"
                        className="block text-xs font-medium text-gray-700 mb-1"
                      >
                        판단 근거 (필수)
                      </label>
                      <textarea
                        id="fd-assess-rationale"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        rows={3}
                        maxLength={4000}
                        value={formRationale}
                        onChange={(e) => setFormRationale(e.target.value)}
                        placeholder="예: EIP 패턴·탄소범위·대조군 비교 결과 등 판단의 근거"
                      />
                    </div>
                    {formError && (
                      <p className="text-sm text-red-600">{formError}</p>
                    )}
                    <button
                      type="submit"
                      disabled={createAssessment.isPending}
                      className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {createAssessment.isPending ? "기록 중…" : "판정 기록"}
                    </button>
                  </form>
                ) : (
                  <p className="text-sm text-gray-500">
                    판정 기록 권한(분석관 이상)이 없습니다. 기록된 판정은 아래
                    이력에서 볼 수 있습니다.
                  </p>
                )}

                {/* 이력 */}
                <div className="mt-4 border-t border-gray-100 pt-3">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    판정 이력
                  </h4>
                  {assessmentsQuery.isLoading ? (
                    <p className="text-xs text-gray-400">불러오는 중…</p>
                  ) : assessmentsQuery.isError ? (
                    <p className="text-xs text-red-500">
                      판정 기록을 불러오지 못했습니다 — "기록 없음"이 아니라
                      조회 실패입니다. 새로고침 후 다시 확인해 주십시오.
                    </p>
                  ) : assessmentsQuery.data &&
                    assessmentsQuery.data.history.length > 0 ? (
                    <ul className="space-y-2">
                      {assessmentsQuery.data.history.map((a) => (
                        <li
                          key={a.id}
                          className="text-xs text-gray-600 bg-gray-50 rounded p-2"
                        >
                          <span
                            className={`inline-flex px-1.5 py-0.5 rounded font-medium mr-2 ${assessmentBadgeClass(a.status)}`}
                          >
                            {ASSESSMENT_STATUS_LABEL[a.status] ?? a.status}
                          </span>
                          <span className="font-medium text-gray-800 mr-2">
                            {screening?.screening.candidates.find(
                              (c) => c.class_key === a.class_key,
                            )?.label_ko ?? a.class_key}
                          </span>
                          {a.supersedes_id != null && (
                            <span className="text-amber-700 mr-2">
                              (판정 #{a.supersedes_id} 정정)
                            </span>
                          )}
                          <span className="text-gray-400 mr-2">
                            {a.assessed_by_username ?? "-"} ·{" "}
                            {a.created_at
                              ? new Date(a.created_at).toLocaleString("ko-KR")
                              : "-"}{" "}
                            · {a.scheme_id}
                          </span>
                          <p className="mt-1 text-gray-700 whitespace-pre-wrap">
                            {a.rationale}
                          </p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-gray-400">
                      기록된 판정이 없습니다. 레거시 공백은 "기록 없음"이
                      사실입니다 — 소급 기입하지 않습니다.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
